import React from 'react'
import * as yup from 'yup'
import { FormikHelpers } from 'formik'
import { useNewTranslation } from 'hooks'
import Modal, { AddAdditionalFeeState, MODAL_TYPE } from './index'
import { generateUuid } from 'services/uuid'
import { getStringLength } from 'components/utils'
import { useDispatch } from 'react-redux'
import { CampaignState, saveAdditionalPayment } from 'state/campaigns'
import { CampaignPaymentSummaryData } from 'services/api/campaigns'

export interface AdditionalFeeProps {
    uuid: string
    title: string
    amount: number
}

interface UpdateAdditionalFeeProps {
    additionalFee: AdditionalFeeProps
    campaign: CampaignState
    type: MODAL_TYPE
    paymentSummary: CampaignPaymentSummaryData
    isTemplate: boolean
    onClose: () => void
}

const UpdateAdditionalFee: React.FC<UpdateAdditionalFeeProps> = ({
    paymentSummary,
    additionalFee,
    type,
    campaign,
    isTemplate,
    onClose,
}) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()

    const handleUpdateAdditionalFee = (values: AdditionalFeeProps) => {
        const { additional_fees: additionalFees = [] } = paymentSummary
        const itemExists = additionalFees?.some(item => item.uuid === values.uuid)

        const updatedValues =
            itemExists && type === MODAL_TYPE.EDIT_PAYMENT_ADJUSTMENT
                ? additionalFees.map(item => (item.uuid === values.uuid ? values : item))
                : [...additionalFees, values]

        dispatch(saveAdditionalPayment({ campaignUuid: campaign.uuid, additionalFees: updatedValues, isTemplate }))

        onClose()
    }

    return (
        <Modal
            initialValues={{
                title: additionalFee ? additionalFee.title : '',
                amount: additionalFee ? additionalFee.amount : null,
            }}
            validationSchema={yup.object({
                title: yup
                    .string()
                    .required(t('modalWindow.editPartipostFee.addtionalFeeName.emptyError'))
                    .test({
                        message: () => t('errorMessage.campaignEditor.textField.enterTaskTitleCharacterLimit'),
                        test: function (value: string): boolean {
                            if (value) {
                                const charLength = getStringLength(value)
                                return !(charLength > 200)
                            }
                            return true
                        },
                    }),
                amount: yup
                    .number()
                    .nullable()
                    .required(t('modalWindow.editPartipostFee.addtionalFeeAmount.emptyError'))
                    .test({
                        message: () => t('modalWindow.editPartipostFee.addtionalFeeAmount.negativeNumber'),
                        test: function (value: number): boolean {
                            if (value) {
                                // const payoutAmount = value + paymentSummary.payable_amount
                                const sumToCheck = paymentSummary.subtotal - additionalFee?.amount
                                const payoutAmount = value + sumToCheck
                                return !(payoutAmount < 0)
                            }
                            return true
                        },
                    }),
            })}
            onSubmit={(values: AddAdditionalFeeState, action: FormikHelpers<AddAdditionalFeeState>) => {
                if (!values.title || !values.amount) return
                const updatedValues = {
                    uuid:
                        type === MODAL_TYPE.EDIT_PAYMENT_ADJUSTMENT && additionalFee
                            ? additionalFee.uuid
                            : generateUuid(),
                    title: values.title,
                    amount: values.amount,
                }
                handleUpdateAdditionalFee(updatedValues)
                onClose()
            }}
            onClose={onClose}
            title={t('modalWindow.addAdditonalFeeTitle')}
            submitButtonText={t('button.confirm')}
            size="m"
        >
            <Modal.TextInput
                name="title"
                label={t('modalWindow.editPartipostFee.addtionalFeeName.title')}
                isRequired
                width="100%"
                placeholder={t('modalWindow.editPartipostFee.addtionalFeeName.placeholder')}
                autoComplete="off"
                showInputLength
                maxInputLength={200}
            />
            <Modal.NumberInput
                name="amount"
                title={t('modalWindow.editPartipostFee.addtionalFeeAmount.title')}
                subtitle={t('modalWindow.editPartipostFee.addtionalFeeAmount.subtext')}
                placeholder={t('modalWindow.editPartipostFee.addtionalFeeAmount.placeholder')}
                isRequired
                width="100%"
            />
        </Modal>
    )
}

export default UpdateAdditionalFee
